import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/auth';
import PasswordRequirements from '../../components/PasswordRequirements/PasswordRequirements';
import logo from '../../assets/logo.jpg';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PublicShell from '../../components/PublicShell/PublicShell';
import './NewPasswordRequired.css';

const NewPasswordRequired = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const email = authService.getTempUserEmail();

  useEffect(() => {
    // Si no hay email temporal, redirigir al login
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'La contraseña debe tener al menos 8 caracteres';
    }
    if (!hasUpperCase) {
      return 'La contraseña debe tener al menos una letra mayúscula';
    }
    if (!hasLowerCase) {
      return 'La contraseña debe tener al menos una letra minúscula';
    }
    if (!hasNumbers) {
      return 'La contraseña debe tener al menos un número';
    }
    if (!hasSpecialChar) {
      return 'La contraseña debe tener al menos un carácter especial';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validar contraseña
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Verificar que las contraseñas coincidan
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    try {
      await authService.completeNewPasswordChallenge(newPassword);
      navigate('/home');
    } catch (err) {
      console.error('Error cambiando contraseña:', err);
      
      let errorMessage = 'Error al cambiar la contraseña';
      
      if (err.code === 'InvalidPasswordException') {
        errorMessage = 'La contraseña no cumple con los requisitos de seguridad';
      } else if (err.code === 'InvalidParameterException') {
        errorMessage = 'Parámetros inválidos. Verifica los datos ingresados';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    authService.clearTempData();
    navigate('/login');
  };

  if (!email) {
    return null; // El useEffect redirigirá
  }

  return (
    <PublicShell>
      <div className="new-password-container">
      <div className="new-password-card">
        <div className="new-password-header">
          <div className="logo-container">
            <img src={logo} alt="Nissa Guard" className="logo-image" />
          </div>
          <h1>Cambio de Contraseña Requerido</h1>
          <p>Debes cambiar tu contraseña temporal para continuar</p>
          <div className="user-info">
            <span>Usuario: {email}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="new-password-form">
          <div className="form-group">
            <label htmlFor="newPassword">Nueva Contraseña</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Ingresa tu nueva contraseña"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Contraseña</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirma tu nueva contraseña"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
              >
                {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </button>
            </div>
          </div>

          <PasswordRequirements password={newPassword} />

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="button-group">
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Cambiando contraseña...' : 'Cambiar Contraseña'}
            </button>
            
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
      </div>
    </PublicShell>
  );
};

export default NewPasswordRequired;
